/**
 * Extracts the GitHub issue ID from a GitHub issue URL
 * @param githubUrl - The GitHub issue URL (e.g., "https://github.com/owner/repo/issues/123")
 * @returns The issue ID as a string, or null if not found
 */
export function extractGithubIssueId(githubUrl: string | null | undefined): string | null {
  if (!githubUrl) return null;
  
  try {
    // Match GitHub issue URLs like:
    // https://github.com/owner/repo/issues/123
    // https://github.com/owner/repo/issues/123#comment
    const issueMatch = githubUrl.match(/github\.com\/[^\/]+\/[^\/]+\/issues\/(\d+)/);
    
    if (issueMatch && issueMatch[1]) {
      return issueMatch[1];
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting GitHub issue ID:', error);
    return null;
  }
}

/**
 * Extracts the repository name from a GitHub URL
 * @param githubUrl - The GitHub URL (e.g., "https://github.com/owner/repo" or "https://github.com/owner/repo/issues/123")
 * @returns The repository name, or null if not found
 */
export function extractGithubRepositoryName(githubUrl: string | null | undefined): string | null {
  if (!githubUrl) return null;
  
  try {
    // Match GitHub URLs like:
    // https://github.com/owner/repo
    // https://github.com/owner/repo/issues/123
    // https://github.com/owner/repo/pull/456
    const repoMatch = githubUrl.match(/github\.com\/[^\/]+\/([^\/]+)/);
    
    if (repoMatch && repoMatch[1]) {
      return repoMatch[1];
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting GitHub repository name:', error);
    return null;
  }
}

/**
 * Extracts the issue number from a GitHub issue URL
 * @param githubUrl - The GitHub issue URL (e.g., "https://github.com/owner/repo/issues/123")
 * @returns The issue number as a number, or null if not found
 */
export function extractGithubIssueNumber(githubUrl: string | null | undefined): number | null {
  const issueId = extractGithubIssueId(githubUrl);
  return issueId ? parseInt(issueId, 10) : null;
}

/**
 * Formats a display ID for a proposal, preferring GitHub issue ID over proposal ID
 * @param proposalId - The proposal UUID
 * @param githubUrl - The GitHub issue URL
 * @returns A formatted display ID
 */
export function formatProposalDisplayId(proposalId: string, githubUrl?: string | null): string {
  const githubIssueId = extractGithubIssueId(githubUrl);
  
  if (githubIssueId) {
    return `#${githubIssueId}`;
  }
  
  // Fallback to showing first 8 characters of UUID
  return `#${proposalId.slice(0, 8)}`;
} 